import { Component, Input, OnInit } from '@angular/core';
import { ModalController, IonicModule } from '@ionic/angular';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { JavapiService } from '../../services/javapi.service';
import { MetodosgenService } from '../../services/metodosgen.service';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-seleccionardatos',
  templateUrl: './seleccionardatos.component.html',
  styleUrls: ['./seleccionardatos.component.scss'],
  standalone: true,
  imports: [CommonModule, IonicModule, FormsModule]
})
export class SeleccionardatosComponent implements OnInit {

  @Input() subrutina: string = '';
  @Input() spr1: string = ''; // Re-adding input to receive configuration
  @Input() tipo: string = ''; // Re-adding input to receive configuration
  @Input() items: any[] = []; // Generic items input
  @Input() titulo: string = 'Seleccionar Opciones'; // Default title
  @Input() initialSelection: any; // Input for persistence

  filteredItems: any[] = [];
  searchText: string = '';

  selectedItems: any[] = [];
  isMultiple: boolean = false;

  constructor(
    private modalCtrl: ModalController,
    private javapiService: JavapiService,
    private metodosGenService: MetodosgenService
  ) { }

  async ngOnInit() {
    // Only 'multiple' type enables multiple selection
    this.isMultiple = this.tipo === 'multiple';

    // Initialize selectedItems from input
    if (this.initialSelection) {
      if (Array.isArray(this.initialSelection)) {
        this.selectedItems = [...this.initialSelection];
      } else if (this.initialSelection) {
        this.selectedItems = [this.initialSelection];
      }
    }

    // Fetch data if subrutina is present
    if (this.subrutina) {
      const data = await this.getDataJson();
      if (Array.isArray(data)) {
        this.items = data;

        // Rehydration: Replace partial objects with full database objects
        if (this.selectedItems.length > 0) {
          this.selectedItems = this.selectedItems.map(sel => {
            const found = this.items.find(item => this.areItemsEqual(sel, item));
            return found ? { ...found } : sel;
          });
        }

        // Sort: Selected items first
        this.items.sort((a, b) => {
          const aSel = this.isChecked(a);
          const bSel = this.isChecked(b);
          if (aSel && !bSel) return -1;
          if (!aSel && bSel) return 1;
          return 0;
        });
      }
    }

    this.filteredItems = [...this.items];
  }

  async getDataJson() {
    try {
      let json = await firstValueFrom(
        this.javapiService.getCommonModel(
          this.metodosGenService.setModeloGeneral(
            "selDtsList", this.subrutina, this.spr1, '', '', '', '', '', '', '', '', '', '', '', '', '', ''
          )
        )
      );
      return json || [];
    } catch (e) {
      console.error('Error fetching data:', e);
      return [];
    }
  }

  @Input() hiddenCols: string[] = []; // Input to hide specific columns

  // Helper to get first 4 non-null values, filtering hidden ones
  getDisplayValues(item: any): string[] {
    const values: string[] = [];
    // Check dt1 to dt4
    for (let i = 1; i <= 4; i++) {
      const key = `dt${i}`;
      // Skip if column is hidden
      if (this.hiddenCols && this.hiddenCols.includes(key)) {
        continue;
      }

      const val = item[key];
      if (val && val !== 'null' && val.trim() !== '') {
        values.push(val);
      }
    }
    // Fallback if not Modelogeneraldatos (e.g. simple object or string)
    if (values.length === 0) {
      if (item.nombre) values.push(item.nombre);
      if (item.descripcion) values.push(item.descripcion);
      if (typeof item === 'string') values.push(item);
    }
    return values;
  }

  dismiss(role: string = 'cancel') {
    this.modalCtrl.dismiss(null, role);
  }

  confirmSelection() {
    // Return single object if not multiple
    const dataToReturn = this.isMultiple ? this.selectedItems : (this.selectedItems[0] || null);

    this.modalCtrl.dismiss({
      action: 'selected',
      data: dataToReturn
    });
  }

  selectItem(item: any) {
    if (this.isMultiple) {
      // Toggle selection for multiple - use refined matching logic
      const index = this.selectedItems.findIndex(i => this.areItemsEqual(i, item));
      if (index > -1) {
        this.selectedItems.splice(index, 1);
      } else {
        this.selectedItems.push(item);
      }
    } else {
      // Single selection - return immediately
      this.modalCtrl.dismiss({
        action: 'selected',
        data: item
      });
    }
  }

  isChecked(item: any): boolean {
    if (!this.selectedItems || this.selectedItems.length === 0) return false;
    return this.selectedItems.some(i => this.areItemsEqual(i, item));
  }

  // Unified matching logic to handle IDs vs Names and avoid inconsistencies
  private areItemsEqual(i1: any, i2: any): boolean {
    const val1 = String(i1.dt1 || i1.id || '').trim().toLowerCase();
    const val2_dt1 = String(i2.dt1 || i2.id || '').trim().toLowerCase();
    const val2_dt2 = String(i2.dt2 || i2.nombre || '').trim().toLowerCase();

    // Match if val1 matches either dt1 or dt2 of i2
    if (val1 && (val1 === val2_dt1 || val1 === val2_dt2)) return true;

    // Check inverse match just in case
    const val2 = String(i2.dt1 || i2.id || '').trim().toLowerCase();
    const val1_dt1 = String(i1.dt1 || i1.id || '').trim().toLowerCase();
    const val1_dt2 = String(i1.dt2 || i1.nombre || '').trim().toLowerCase();
    if (val2 && (val2 === val1_dt1 || val2 === val1_dt2)) return true;

    // Fallback to JSON for non-model objects
    return JSON.stringify(i1) === JSON.stringify(i2);
  }

  search(event: any) {
    const query = event.detail.value ? event.detail.value.toLowerCase() : '';
    this.searchText = query;

    if (query.trim() === '') {
      this.filteredItems = [...this.items];
    } else {
      this.filteredItems = this.items.filter(item => {
        // Search in the display values first (dt1-dt4)
        const displayVals = this.getDisplayValues(item);
        return displayVals.some(v => v.toLowerCase().includes(query));
      });
    }
  }

}
